import { createContext, useContext, type PropsWithChildren } from 'react';
import { useSnapshot } from 'valtio/react';
import {
  DEFAULT_MAGNETIC_NODE_SNAPSHOT,
  type MagneticHandlesSession,
  type MagneticHandlesStoreState,
} from './use-magnetic-handles-session';

type MagneticHandlesContextValue = {
  session: MagneticHandlesSession;
  viewportMoving: boolean;
};

const MagneticHandlesContext = createContext<MagneticHandlesContextValue | null>(null);

export function MagneticHandlesProvider({
  children,
  session,
  viewportMoving = false,
}: PropsWithChildren<{
  session: MagneticHandlesSession;
  viewportMoving?: boolean;
}>) {
  return <MagneticHandlesContext.Provider value={{ session, viewportMoving }}>{children}</MagneticHandlesContext.Provider>;
}

function useMagneticHandlesContext() {
  const context = useContext(MagneticHandlesContext);
  if (!context) {
    throw new Error('MagneticConnectionHandles must be used within MagneticHandlesProvider.');
  }

  return context;
}

export function useMagneticHandlesSelector<T>(selector: (state: MagneticHandlesStoreState) => T): T {
  const { session } = useMagneticHandlesContext();
  const snap = useSnapshot(session.store);
  return selector(snap as MagneticHandlesStoreState);
}

export function useMagneticHandleNodeState(nodeId: string) {
  return useMagneticHandlesSelector((state) => (
    state.nodeSnapshots[nodeId] ?? DEFAULT_MAGNETIC_NODE_SNAPSHOT
  ));
}

export function useMagneticHandlesRuntime() {
  const { session } = useMagneticHandlesContext();
  const connectingFromHandleType = useMagneticHandlesSelector((state) => state.connectingFromHandleType);
  const connectingFromNodeId = useMagneticHandlesSelector((state) => state.connectingFromNodeId);

  return {
    connectingFromHandleType,
    connectingFromNodeId,
    runtime: session,
  };
}

export function useMagneticHandlesViewportMoving() {
  const { viewportMoving } = useMagneticHandlesContext();
  return viewportMoving;
}
